import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useContent } from "@/data/ContentContext";
import {
  ShieldCheck, BadgeCheck, ScrollText, Leaf, Handshake,
  Building2, HardHat, Zap, Factory, Cog, Layers, ClipboardCheck, Sprout,
  ArrowRight, ArrowDown, Sparkles, Workflow,
  Search, Wrench, Rocket, ChevronDown, Check,
  Users, MessageSquare, Target, LineChart, FileText, PenLine, Presentation
} from "lucide-react";

// Reusable animated counter
const Counter = ({ to, suffix = "", prefix = "", duration = 1600 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
};

// Animated background grid / network
const NetworkBg = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="tuv-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M60 0H0V60" fill="none" stroke="rgba(15,15,14,0.08)" strokeWidth="1"/>
        </pattern>
        <radialGradient id="tuv-spot" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="rgba(194,65,12,0.18)"/>
          <stop offset="100%" stopColor="rgba(194,65,12,0)"/>
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#tuv-grid)"/>
      <rect width="100%" height="100%" fill="url(#tuv-spot)"/>
    </svg>
    {/* Floating dots */}
    {[...Array(14)].map((_, i) => (
      <motion.span
        key={i}
        className="absolute w-1.5 h-1.5 rounded-full bg-[var(--accent)]/60"
        style={{ left: `${(i * 73) % 100}%`, top: `${(i * 47) % 100}%` }}
        animate={{ y: [0, -14, 0], opacity: [0.3, 0.9, 0.3] }}
        transition={{ duration: 4 + (i % 4), repeat: Infinity, delay: i * 0.2 }}
      />
    ))}
  </div>
);

const SectionEyebrow = ({ children, num }) => (
  <div className="flex items-baseline gap-3 mb-4">
    {num && <span className="font-mono text-xs tracking-widest text-[var(--accent)]">{num}</span>}
    <span className="overline">{children}</span>
  </div>
);

const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

// Live GMT date — formatted "13 February 2026"
const LiveDate = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // refresh at next midnight GMT so the date stays live for long sessions
    const check = () => setNow(new Date());
    const id = setInterval(check, 60 * 60 * 1000); // hourly is plenty
    return () => clearInterval(id);
  }, []);
  const formatted = now.toLocaleDateString("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return <span>{formatted}</span>;
};

const THEMES = [
  { icon: ShieldCheck, label: "Safety" },
  { icon: BadgeCheck, label: "Quality" },
  { icon: ScrollText, label: "Compliance" },
  { icon: Leaf, label: "Sustainability" },
  { icon: Handshake, label: "Trust" },
];

const INDUSTRIES = [
  { icon: Building2, label: "Buildings & Infrastructure" },
  { icon: HardHat, label: "Construction" },
  { icon: Zap, label: "Energy" },
  { icon: Factory, label: "Manufacturing" },
  { icon: Cog, label: "Industrial Plants" },
  { icon: Layers, label: "Asset Integrity" },
  { icon: ClipboardCheck, label: "Technical Inspection" },
  { icon: Sprout, label: "Sustainability" },
];

const FLOW = [
  { label: "Our priorities", icon: Target, note: "What we set out to achieve" },
  { label: "Our strategy", icon: Workflow, note: "How we get there" },
  { label: "Our messaging", icon: MessageSquare, note: "What we say to whom" },
  { label: "Our campaigns", icon: Rocket, note: "Where audiences meet us" },
];

// Section 3 experience vignettes — icon-driven mini-scenes
const EXPERIENCE = [
  { label: "Stakeholder workshops", icon: Users, motif: "workshop" },
  { label: "Marketing strategy", icon: Target, motif: "target" },
  { label: "Commercial planning", icon: LineChart, motif: "chart" },
  { label: "Audience profiling", icon: Search, motif: "audience" },
  { label: "Brand messaging", icon: MessageSquare, motif: "message" },
  { label: "Content strategy", icon: PenLine, motif: "content" },
  { label: "Performance optimisation", icon: Workflow, motif: "optimise" },
];

const AI_JOURNEY = [
  { label: "Discover", icon: Search },
  { label: "Assess", icon: FileText },
  { label: "Identify", icon: Sparkles },
  { label: "Build", icon: Wrench },
  { label: "Implement", icon: Rocket },
  { label: "Optimise", icon: Workflow },
];

const AI_BRANCHES = ["Marketing", "Operations", "Recruitment", "HR", "Content", "Planning"];

// Marketing scenario planning — 6-step framework with tabs
const SCENARIO_STEPS = [
  {
    n: "01",
    key: "clarify",
    label: "Clarify",
    title: "Clarifying the brief",
    intro: "Before writing anything, get the strategic context.",
    bullets: [
      "What business problem are we trying to solve?",
      "How does this sit into the business strategy overall?",
      "Is it a priority? Why now?",
      "Who is the audience? (Is this also the customer?)",
      "What does success look like — reviewed as part of wider, longer-term strategy too?",
      "What is the timeframe?",
      "Are there budget or resource constraints?",
      "What has been done to date, if anything?",
    ],
  },
  {
    n: "02",
    key: "audience",
    label: "Audience",
    title: "Understanding the audience",
    intro: "Stakeholder mapping — the buyer, the customer group, and the audience to reach.",
    bullets: [
      "Who makes the buying decision?",
      "Who influences it?",
      "What problem are they trying to solve?",
      "What's stopping them buying today?",
      "Where do they currently seek information?",
      "What keeps them up at night — and what does their success look like?",
    ],
  },
  {
    n: "03",
    key: "review",
    label: "Review",
    title: "Review the information",
    intro: "It doesn't need to be extensive, but should draw from both internal and external inputs.",
    groups: [
      { label: "Internal", items: ["Sales feedback", "CRM", "Existing enquiries", "Website data", "Previous campaigns", "Customer feedback"] },
      { label: "External", items: ["Competitors", "Market trends", "Industry challenges", "Search behaviour", "Customer activity"] },
    ],
  },
  {
    n: "04",
    key: "prioritise",
    label: "Prioritise",
    title: "Prioritise activity",
    intro: "Develop a brief for activity based on what's been reviewed and what it means for the best action plan.",
    note: "Order matters — objective, strategy and audience come first. Channels and the intent behind their use come second.",
    sequence: [
      { label: "Objective", note: "What business outcome" },
      { label: "Strategy", note: "How we get there" },
      { label: "Audience", note: "Who we speak to" },
      { label: "Channels", note: "Where they meet us" },
    ],
  },
  {
    n: "05",
    key: "align",
    label: "Align",
    title: "Align with internal teams",
    intro: (
      "Align with sales before the brief goes out and while the brief review is underway — to ensure " +
      "messaging in real-world sales conversations is valid and to review what a good sales lead or " +
      "interaction actually looks like."
    ),
    bullets: [
      "Review, refine and keep an open feedback loop.",
      "Test messaging with the people who use it every day.",
      "Ensure sales-marketing agreement on definitions (MQL, SQL, opportunity).",
    ],
  },
  {
    n: "06",
    key: "measure",
    label: "Measure",
    title: "Measure success",
    intro: "Combine leading indicators (weeks) with lagging indicators (months) for a fuller picture.",
    groups: [
      { label: "Leading (weeks)", items: ["Website engagement", "Content downloads", "Webinar registrations", "Marketing qualified leads", "Sales conversations generated", "Share of voice"], caption: "Is the strategy gaining traction?" },
      { label: "Lagging (months)", items: ["SQLs", "Pipeline value", "Win rate", "Revenue", "Customer retention", "Market share", "Brand preference"], caption: "Did it deliver commercial impact?" },
    ],
  },
];

const CAMPAIGN_PHASES = [
  { phase: "Launch", objective: "Build awareness and educate the market", measures: "Reach, engagement, content consumption, webinar attendance" },
  { phase: "Growth", objective: "Generate demand and nurture interest", measures: "MQLs, SQLs, meetings booked, conversion rates" },
  { phase: "Maturity", objective: "Drive commercial performance and optimise", measures: "Pipeline, revenue, ROI, customer retention" },
];

const MarketingScenarioPlanning = () => {
  const [active, setActive] = useState(0);
  const step = SCENARIO_STEPS[active];

  return (
    <div className="mt-20 border-t border-black/10 pt-16" data-testid="scenario-planning">
      <div className="overline mb-4">— Marketing scenario planning</div>
      <Reveal>
        <h3 className="editorial-h2 text-3xl md:text-5xl mb-4 max-w-4xl">A repeatable framework — from brief to measurable impact.</h3>
      </Reveal>
      <Reveal delay={0.06}>
        <p className="text-base md:text-lg text-black/65 max-w-3xl mb-10 leading-relaxed">
          The way I approach any new marketing scenario — a discipline I use to translate business
          objectives into commercially-focused campaigns.
        </p>
      </Reveal>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-2 mb-8" role="tablist" data-testid="scenario-tabs">
        {SCENARIO_STEPS.map((s, i) => {
          const isActive = active === i;
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(i)}
              className={`group inline-flex items-center gap-2.5 px-4 py-2.5 border transition-all text-xs uppercase tracking-[0.18em] font-semibold ${
                isActive
                  ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                  : "bg-white/60 text-black/70 border-black/15 hover:border-black hover:text-black"
              }`}
              data-testid={`scenario-tab-${s.key}`}
            >
              <span className={`font-mono ${isActive ? "text-[#F2DACB]" : "text-[#C2410C]"}`}>{s.n}</span>
              <span>{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content panel */}
      <motion.div
        key={step.key}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-12 gap-6 md:gap-10 bg-white/60 border border-black/10 p-6 md:p-10"
      >
        <div className="col-span-12 md:col-span-4">
          <div className="font-mono text-xs tracking-widest text-[#C2410C] mb-3">STEP {step.n}</div>
          <div className="font-display text-3xl md:text-4xl leading-tight">{step.title}</div>
          <p className="mt-4 text-sm md:text-base text-black/70 leading-relaxed">{step.intro}</p>
          {step.note && (
            <div className="mt-4 border-l-2 border-[#C2410C] pl-4 text-sm text-black/75 italic">
              {step.note}
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-8">
          {/* Simple bulleted list */}
          {step.bullets && (
            <ul className="space-y-2.5">
              {step.bullets.map((b, idx) => (
                <motion.li
                  key={idx}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04, duration: 0.35 }}
                  className="flex gap-3 text-sm md:text-base leading-relaxed border-b border-black/10 pb-2.5 last:border-0"
                >
                  <span className="text-[#C2410C] font-mono shrink-0 mt-0.5">↳</span>
                  <span>{b}</span>
                </motion.li>
              ))}
            </ul>
          )}

          {/* Two-column groups (internal/external, leading/lagging) */}
          {step.groups && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {step.groups.map((g, gi) => (
                <motion.div
                  key={g.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: gi * 0.08, duration: 0.4 }}
                  className="bg-[var(--bg)] border border-black/10 p-5"
                >
                  <div className="overline mb-3">— {g.label}</div>
                  <ul className="space-y-1.5">
                    {g.items.map((it, ii) => (
                      <li key={ii} className="flex gap-2.5 text-sm">
                        <span className="text-[#C2410C]">·</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  {g.caption && (
                    <div className="mt-3 pt-3 border-t border-black/10 text-xs italic text-black/60">
                      {g.caption}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Sequence visualisation (step 04) */}
          {step.sequence && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {step.sequence.map((s, si) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: si * 0.08, duration: 0.4 }}
                  className="bg-[var(--bg)] border border-black/10 p-4 relative"
                >
                  <div className="font-mono text-[10px] tracking-widest text-[#C2410C]">0{si + 1}</div>
                  <div className="font-display text-lg mt-1">{s.label}</div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-black/50 mt-1">{s.note}</div>
                  {si < step.sequence.length - 1 && (
                    <ArrowRight
                      size={16}
                      className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-black/30 bg-[var(--bg)]"
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Campaign phases table */}
      <div className="mt-12">
        <div className="overline mb-3">— One useful concept</div>
        <p className="text-base md:text-lg text-black/70 max-w-3xl mb-5 leading-relaxed">
          <strong>Campaign phases.</strong> Not every campaign should deliver the same outcomes at every stage.
        </p>
        <div className="overflow-x-auto border border-black/15">
          <table className="w-full text-left text-sm md:text-base">
            <thead className="bg-[var(--ink)] text-[var(--bg)]">
              <tr>
                <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">Phase</th>
                <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">Marketing Objective</th>
                <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">Success Measures</th>
              </tr>
            </thead>
            <tbody>
              {CAMPAIGN_PHASES.map((row, i) => (
                <tr key={row.phase} className={`border-t border-black/10 ${i % 2 === 0 ? "bg-white/60" : "bg-white/30"}`}>
                  <td className="p-4 font-display text-lg">{row.phase}</td>
                  <td className="p-4 leading-snug">{row.objective}</td>
                  <td className="p-4 leading-snug text-black/75">{row.measures}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CASES_FALLBACK = [
  {
    id: "expo",
    title: "Expo City Dubai",
    tag: "Marketing campaign I'm proud of",
    year: "2019—2024",
    summary:
      "Appointed by Expo 2020 Dubai to deliver the vision for District 2020 — the legacy phase for Dubai's innovation district. A multi-million-pound programme coordinating 25+ years of engagement for a 47M+ sq. ft sustainable innovation district.",
    highlights: [
      "Led on the multi-million-pound legacy contract from 2019 onward.",
      "Delivered the future-district vision through immersive and interactive experiences.",
      "Digital assets used for site design reviews with leadership and architects.",
      "Marketing and sales campaigns supporting remote onboarding of estate owners, tenants and investors.",
    ],
  },
  {
    id: "uk",
    title: "UK Government — Official Digital Partner",
    tag: "Partnership development",
    year: "2018—2021",
    summary:
      "Won the appointment of our organisation as the Official UK Digital Partner in 2018. Led a UK-wide call for digital collaboration on behalf of the Government with high-profile investment opportunities — launching with stakeholder engagement at the UK Pavilion at MIPIM in Cannes.",
    highlights: [
      "UK Pavilion at MIPIM 2019 — VR/AR-led investor engagement.",
      "Showcased UK architecture at its best to global investors and ministers.",
      "Scope evolved into remote engagement tools during COVID-19 restrictions.",
      "Presented programme outcomes to the UK Minister for Investment.",
    ],
  },
  {
    id: "rsg",
    title: "Red Sea Global (PIF)",
    tag: "A project that didn't go to plan",
    year: "2022—2023",
    overview:
      "Appointed to develop an immersive residential sales experience for Red Sea Global, combining interactive hardware with a digital sales platform to support off-plan property sales. The project focused on helping stakeholders visualise developments remotely and creating a more engaging customer sales journey.",
    challenges: [
      "Planning and design had to be completed without regular access to the live development site.",
      "Introducing immersive technology to a client team with little previous exposure required more education and change management than anticipated.",
      "Adoption of new digital asset workflows was slower than expected, creating frustration within the sales team.",
      "A virtual version of the sales suite was developed to demonstrate future plans, but engagement levels remained below expectations.",
    ],
    outcome:
      "The project successfully supported design reviews and planning activities through immersive visualisation. However, the wider residential sales platform and immersive customer experience were not adopted as part of the final delivery, with the sales engagement platform awarded elsewhere.",
    learnings: [
      "New technology requires structured onboarding for both internal teams and clients to build confidence and drive adoption.",
      "Early stakeholder mapping helps identify knowledge gaps and informs more effective engagement.",
      "Remote delivery of hardware-based solutions benefits from clearer operational processes and regional support.",
      "Service offerings should be continually reviewed to ensure they align with customer readiness as well as technical capability.",
      "Digital asset planning, software development and operational delivery must be tightly integrated to maximise project success.",
    ],
  },
];

// Images stay in code (paths keyed by case id)
const CASE_IMAGES = {
  expo: [
    "/media/img/d2020-cityscape-3.jpg",
    "/media/img/d2020-cityscape-1.jpg",
    "/media/img/d2020-cityscape-5.jpg",
  ],
  uk: [
    "/media/img/uk-gov-mipim.jpg",
    "/media/img/minister.jpg",
    "/media/img/mipim-team.jpg",
  ],
  rsg: [
    "/media/img/rsg-sheybarah.webp",
    "/media/img/rsg-boardwalk.webp",
    "/media/img/rsg-marina.webp",
  ],
};

// Fallback defaults — used until MongoDB /api/content resolves the `interviews` array
const INTERVIEW_FALLBACK = {
  slug: "tuvsud",
  company: "TÜV SÜD",
  companyBadge: "TÜV·SÜD",
  companyColor: "#005AA0",
  heroHeadingLine1: "Marketing Business Partner",
  heroHeadingLine2: "Industry & Infrastructure",
  topBarLabel: "For TÜV SÜD",
  showSection1: true,
  showInterviewers: true,
  interviewers: [
    { name: "Peter Schmidt", role: "Head of Marketing Business Partner" },
    { name: "Rob Greenwood", role: "Head of Sales & Marketing Performance" },
  ],
  hero: {
    intro:
      "This page captures my understanding of the business, my approach to the role, and examples of directly relevant experience — a short, interactive companion to our conversation.",
  },
  questions: [
    "Where are we growing?",
    "Which markets offer the greatest opportunity?",
    "Which customers should we prioritise?",
    "Which services drive growth?",
    "What matters most to customers?",
    "How do customers buy?",
    "How do we differentiate?",
    "How can marketing accelerate sales?",
    "Which partnerships create opportunity?",
    "How do we measure impact?",
  ],
  strategyPriorities: [
    "Scale and strengthen the core business",
    "Develop innovative, future-focused services",
    "Support customers' sustainability transformation",
    "Deliver operational and customer excellence",
    "Invest in people, culture and digital capability",
  ],
  strengths: [
    "Commercial mindset",
    "Strategic marketing",
    "Trusted relationships",
    "Senior stakeholder influence",
    "Translating complexity",
    "International collaboration",
    "Cross-functional leadership",
    "AI & innovation",
  ],
  cases: CASES_FALLBACK,
};

const STRATEGY_MEANS = [
  "Evolving from a traditional TIC organisation into a broader technology and assurance partner",
  "Significant investment in digitalisation, AI and global collaboration",
  "Greater international alignment and operational consistency",
  "Helping customers adopt emerging technologies safely and with confidence",
];

const COMPETITORS = [
  { name: "SGS", hq: "Geneva, Switzerland", diff: "World's largest TIC company with exceptional global reach across almost every industry." },
  { name: "Bureau Veritas", hq: "Paris, France", diff: "Strong sustainability, marine, construction and certification expertise." },
  { name: "Intertek", hq: "London, UK", diff: "Product assurance specialist with a particularly strong consumer products and retail focus." },
  { name: "DNV", hq: "Høvik, Norway", diff: "Deep technical expertise in energy, renewables, maritime and digital assurance." },
  { name: "DEKRA", hq: "Stuttgart, Germany", diff: "Best known for automotive testing and mobility, while expanding into industrial inspection and digital trust." },
];

// Animated flow diagram: Priorities -> Strategy -> Messaging -> Campaigns
const FlowDiagram = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });

  return (
    <div ref={ref} className="relative">
      {/* Desktop: horizontal path + cards */}
      <div className="hidden md:block relative">
        {/* SVG connecting line that draws in */}
        <svg
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none"
          viewBox="0 0 1000 60"
          preserveAspectRatio="none"
          height="60"
          width="100%"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="flow-line" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0F0F0E" />
              <stop offset="50%" stopColor="#C2410C" />
              <stop offset="100%" stopColor="#0F0F0E" />
            </linearGradient>
          </defs>
          <motion.path
            d="M 40 30 L 960 30"
            stroke="url(#flow-line)"
            strokeWidth="1.5"
            strokeDasharray="6 6"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>

        <div className="relative grid grid-cols-4 gap-6">
          {FLOW.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 0.6, delay: 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="group relative"
              >
                {/* Node marker */}
                <div className="flex justify-center mb-4">
                  <motion.div
                    className="relative w-11 h-11 rounded-full bg-white border-2 border-black flex items-center justify-center shadow-sm"
                    initial={{ scale: 0 }}
                    animate={inView ? { scale: 1 } : { scale: 0 }}
                    transition={{ duration: 0.5, delay: 0.35 + i * 0.18, type: "spring", stiffness: 220, damping: 18 }}
                  >
                    <Icon size={18} className="text-black" strokeWidth={2} />
                    {/* Pulsing dot on last node */}
                    {i === FLOW.length - 1 && (
                      <motion.span
                        className="absolute inset-0 rounded-full border-2 border-[#C2410C]"
                        animate={{ scale: [1, 1.5, 1], opacity: [0.8, 0, 0.8] }}
                        transition={{ duration: 2.4, repeat: Infinity, delay: 1.6 }}
                      />
                    )}
                  </motion.div>
                </div>

                <div className="bg-[var(--ink)] text-[var(--bg)] p-5 text-center group-hover:bg-black transition-colors">
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-mono">Step 0{i + 1}</div>
                  <div className="font-display text-xl mt-1.5">{f.label}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#F2DACB] mt-2 opacity-80">{f.note}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden flex flex-col gap-3">
        {FLOW.map((f, i) => {
          const Icon = f.icon;
          return (
            <Reveal key={f.label} delay={i * 0.08}>
              <div className="flex items-stretch">
                <div className="flex flex-col items-center mr-4">
                  <div className="w-10 h-10 rounded-full border-2 border-black bg-white flex items-center justify-center shrink-0">
                    <Icon size={16} strokeWidth={2} />
                  </div>
                  {i < FLOW.length - 1 && (
                    <div className="flex-1 w-px bg-black/30 my-1" />
                  )}
                </div>
                <div className="flex-1 bg-[var(--ink)] text-[var(--bg)] px-5 py-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-mono">Step 0{i + 1}</div>
                  <div className="font-display text-lg mt-0.5">{f.label}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#F2DACB] opacity-80 mt-1">{f.note}</div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
};

// Icon-driven experience vignette — small animated scenes
const ExperienceVignette = ({ item, index }) => {
  const Icon = item.icon;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  // Motif-specific decorations
  const decor = {
    workshop: (
      <g>
        {[0, 1, 2, 3].map((n) => (
          <motion.circle
            key={n}
            cx={16 + n * 12} cy="46" r="3"
            fill="#C2410C"
            initial={{ opacity: 0, y: 6 }}
            animate={inView ? { opacity: [0, 1, 0.7], y: [6, 0, 0] } : {}}
            transition={{ duration: 0.6, delay: 0.3 + n * 0.1 }}
          />
        ))}
      </g>
    ),
    target: (
      <g>
        {[18, 12, 6].map((r, i) => (
          <motion.circle
            key={r}
            cx="30" cy="30" r={r}
            fill="none" stroke="#C2410C" strokeWidth="1"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={inView ? { opacity: 0.8, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 + i * 0.12 }}
          />
        ))}
        <motion.circle
          cx="30" cy="30" r="2" fill="#0F0F0E"
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.7 }}
        />
      </g>
    ),
    chart: (
      <g>
        {[
          [10, 40], [22, 32], [34, 36], [46, 22], [56, 26]
        ].map((p, i, arr) => (
          <g key={i}>
            <motion.circle
              cx={p[0]} cy={p[1]} r="2.5" fill="#C2410C"
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
            />
            {i < arr.length - 1 && (
              <motion.line
                x1={p[0]} y1={p[1]} x2={arr[i + 1][0]} y2={arr[i + 1][1]}
                stroke="#0F0F0E" strokeWidth="1.2"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
              />
            )}
          </g>
        ))}
      </g>
    ),
    audience: (
      <g>
        {[
          [16, 34, 6], [30, 26, 8], [46, 34, 6]
        ].map((c, i) => (
          <motion.circle
            key={i}
            cx={c[0]} cy={c[1]} r={c[2]}
            fill="none" stroke={i === 1 ? "#C2410C" : "#0F0F0E"} strokeWidth="1.2"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.35 + i * 0.12 }}
          />
        ))}
        <motion.circle
          cx="30" cy="26" r="2" fill="#C2410C"
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ delay: 0.9 }}
        />
      </g>
    ),
    message: (
      <g>
        <motion.path
          d="M 10 22 L 42 22 L 42 38 L 22 38 L 16 44 L 16 38 L 10 38 Z"
          fill="none" stroke="#0F0F0E" strokeWidth="1.2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={inView ? { pathLength: 1, opacity: 1 } : {}}
          transition={{ duration: 0.9, delay: 0.3 }}
        />
        {[18, 24, 30].map((x, i) => (
          <motion.circle
            key={x} cx={x} cy="30" r="1.4" fill="#C2410C"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: [0, 1, 1] } : {}}
            transition={{ duration: 0.4, delay: 1.1 + i * 0.15 }}
          />
        ))}
      </g>
    ),
    content: (
      <g>
        {[16, 22, 28, 34].map((y, i) => (
          <motion.line
            key={y}
            x1="12" y1={y} x2={i === 3 ? 34 : 48} y2={y}
            stroke="#0F0F0E" strokeWidth="1.2"
            initial={{ pathLength: 0 }}
            animate={inView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.35 + i * 0.1 }}
          />
        ))}
        <motion.path
          d="M 40 32 L 48 24"
          stroke="#C2410C" strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.9 }}
        />
      </g>
    ),
    optimise: (
      <g>
        <motion.path
          d="M 12 40 Q 22 20 32 30 T 52 22"
          fill="none" stroke="#0F0F0E" strokeWidth="1.4"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 1, delay: 0.3 }}
        />
        <motion.circle
          cx="52" cy="22" r="3" fill="#C2410C"
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ delay: 1.2 }}
        />
      </g>
    ),
  }[item.motif];

  return (
    <div ref={ref} className="group relative bg-white/70 border border-black/10 p-5 hover:border-black hover:-translate-y-1 transition-all overflow-hidden">
      {/* Corner index */}
      <div className="absolute top-3 right-3 text-[10px] font-mono text-black/40">
        {String(index + 1).padStart(2, "0")}
      </div>

      {/* Animated SVG scene */}
      <svg viewBox="0 0 60 60" className="w-full h-14 mb-3" aria-hidden="true">
        {decor}
      </svg>

      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[#C2410C]" strokeWidth={2} />
        <div className="font-semibold text-sm leading-snug">{item.label}</div>
      </div>

      {/* Hover underline sweep */}
      <span
        aria-hidden="true"
        className="absolute left-0 bottom-0 h-[2px] w-0 bg-[#C2410C] transition-all duration-500 group-hover:w-full"
      />
    </div>
  );
};

// AI Transformation Hub — real screenshot showcase with tab switcher
const AiHubShowcase = () => {
  const SHOTS = [
    { key: "persona", label: "Persona Design", src: "/media/img/aihub-persona.webp", caption: "Structured AI Persona Architecture" },
    { key: "playbooks", label: "Playbooks", src: "/media/img/aihub-playbooks.webp", caption: "Departmental AI Implementation Playbooks" },
  ];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-rotate every 5s unless the user has interacted
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % SHOTS.length), 5000);
    return () => clearInterval(id);
  }, [SHOTS.length, paused]);

  const selectTab = (i) => {
    setActive(i);
    setPaused(true);
  };

  return (
    <div className="relative">
      {/* Soft ambient glow */}
      <div
        aria-hidden="true"
        className="absolute -inset-6 bg-[#C2410C]/10 blur-3xl -z-10 rounded-full"
      />

      <div className="relative bg-[#0B0B0A] border border-black/50 rounded-lg overflow-hidden shadow-[0_40px_90px_-25px_rgba(0,0,0,0.7)]">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-black/60 border-b border-white/10">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/60">
            <Sparkles size={12} className="text-[#F2DACB]" />
            <span>AI Transformation Hub · Live prototype</span>
          </div>
        </div>

        {/* Screenshot stage */}
        <div className="relative aspect-[16/10] bg-white overflow-hidden">
          {SHOTS.map((s, i) => (
            <motion.img
              key={s.key}
              src={s.src}
              alt={s.caption}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-top"
              initial={false}
              animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.02 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            />
          ))}
          {/* Bottom caption overlay */}
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">Screen</div>
            <div className="text-white text-sm font-semibold">{SHOTS[active].caption}</div>
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex border-t border-white/10 bg-black/40">
          {SHOTS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => selectTab(i)}
              className={`flex-1 px-3 py-2.5 text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                active === i ? "text-white bg-[#C2410C]/40" : "text-white/50 hover:text-white/80"
              }`}
              data-testid={`aihub-tab-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Caption below */}
      <div className="mt-4 flex items-start gap-3 text-xs text-black/60">
        <Sparkles size={14} className="text-[#C2410C] shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          Screens from the AI Transformation Hub prototype — a working system I've built to guide SMEs from
          understanding AI to deploying tailored playbooks and personas.
        </p>
      </div>
    </div>
  );
};

const TuvSud = ({ slug: slugProp }) => {
  const [openCase, setOpenCase] = useState(null);
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 600], [0, -80]);
  const { content } = useContent();
  const params = useParams();
  const slug = slugProp || params.slug || "tuvsud";

  // Look up interview by slug in the CMS `interviews` array
  const interviews = content?.interviews || [];
  const found = interviews.find((it) => (it.slug || "").toLowerCase() === slug.toLowerCase());
  // Merge with fallback so preview never breaks + defaults for new fields
  const tuv = { ...INTERVIEW_FALLBACK, ...(found || {}) };
  const questions = tuv.questions?.length ? tuv.questions : INTERVIEW_FALLBACK.questions;
  const strategyPriorities = tuv.strategyPriorities?.length ? tuv.strategyPriorities : INTERVIEW_FALLBACK.strategyPriorities;
  const strengthsList = tuv.strengths?.length ? tuv.strengths : INTERVIEW_FALLBACK.strengths;
  // Merge each case: keep images from code + text from CMS
  const cases = (tuv.cases?.length ? tuv.cases : CASES_FALLBACK).map((c) => ({
    ...c,
    images: CASE_IMAGES[c.id] || [],
  }));

  useEffect(() => {
    document.title = `Mia O'Neill · ${tuv.heroHeadingLine1} — ${tuv.company}`;
  }, [tuv.heroHeadingLine1, tuv.company]);

  // 404-like state when slug points to a non-existent interview (once content has loaded)
  if (content && interviews.length > 0 && !found) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--ink)] px-6 text-center">
        <div>
          <div className="overline text-black/50 mb-3">— Interview presentation</div>
          <h1 className="font-display text-4xl md:text-6xl mb-4">Not found</h1>
          <p className="text-black/60 mb-8">
            No interview page exists at <code className="bg-black/5 px-2 py-1 text-sm">/interview/{slug}</code>.
          </p>
          <a href="/" className="inline-flex items-center gap-2 border border-black px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-black hover:text-[var(--bg)] transition-colors">
            Back to main site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-grain bg-[var(--bg)] text-[var(--ink)] min-h-screen" data-testid="tuvsud-page">
      {/* Top slim bar (no site nav) */}
      <div className="fixed top-0 inset-x-0 z-40 bg-[var(--bg)]/85 backdrop-blur border-b border-black/10">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-lg">Mia O'Neill</span>
            <span className="overline text-black/50 hidden sm:inline">— Interview Presentation</span>
          </div>
          <div className="flex items-center gap-4">
            {tuv.topBarLabel && (
              <span className="overline text-[var(--accent)] hidden md:inline">{tuv.topBarLabel}</span>
            )}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold border border-black px-3 py-1.5 hover:bg-black hover:text-[var(--bg)] transition-colors"
              data-testid="tuv-visit-main"
            >
              Visit main site <ArrowRight size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative pt-40 md:pt-48 pb-24 md:pb-32 px-6 md:px-10 lg:px-14 overflow-hidden">
        <NetworkBg />
        <motion.div style={{ y: heroParallax }} className="relative z-10 max-w-[1280px] mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6">
              <span className="font-display text-2xl md:text-3xl">Mia O'Neill</span>
              <span className="w-8 h-px bg-black/40" aria-hidden="true" />
              {tuv.companyBadge && (
                <span
                  className="inline-flex items-center gap-2 border-2 px-3 py-1.5 font-display tracking-tight text-lg md:text-xl"
                  style={{ borderColor: tuv.companyColor || "#005AA0", color: tuv.companyColor || "#005AA0" }}
                >
                  {tuv.companyBadge}
                </span>
              )}
            </div>
          </Reveal>
          <Reveal>
            <span className="overline text-[#C2410C]">Confidential · Interview Presentation</span>
          </Reveal>

          {/* Presented For — interviewer plate (CMS driven) */}
          {tuv.showInterviewers && tuv.interviewers?.length > 0 && (
            <Reveal delay={0.05}>
              <div className="mt-5 max-w-3xl">
                <div className="relative border border-black/15 bg-white/60 backdrop-blur-sm px-5 py-4 md:px-6 md:py-5">
                  {/* Corner marker */}
                  <span
                    aria-hidden="true"
                    className="absolute -top-px left-0 h-px w-16 bg-[#C2410C]"
                  />
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-black/50 font-mono mb-2">
                        Presented for
                      </div>
                      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                        {tuv.interviewers.map((p, idx) => (
                          <div key={idx} className="flex items-start gap-6">
                            {idx > 0 && (
                              <span className="hidden md:block w-px self-stretch bg-black/15" aria-hidden="true" />
                            )}
                            <div>
                              <div className="font-display text-lg md:text-xl leading-tight">{p.name}</div>
                              <div className="text-[11px] uppercase tracking-[0.18em] text-black/55 mt-0.5">
                                {p.role}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="md:text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-black/50 font-mono mb-1">
                        Prepared
                      </div>
                      <div className="font-display text-base md:text-lg" data-testid="tuv-live-date">
                        <LiveDate />
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-black/45 mt-0.5">GMT</div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          )}
          <Reveal delay={0.08}>
            <h1 className="hero-word text-[10vw] sm:text-[8vw] md:text-[6vw] lg:text-[5vw] xl:text-[4.6vw] mt-4">
              {tuv.heroHeadingLine1}
              {tuv.heroHeadingLine2 && (
                <>
                  <br />
                  <span className="text-[var(--accent)]">{tuv.heroHeadingLine2}</span>
                </>
              )}
            </h1>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-6 text-base md:text-lg text-black/65 max-w-2xl leading-relaxed">
              {tuv.hero?.intro}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold text-black/60">
              <ArrowDown size={16} className="animate-bounce-soft" />
              Explore
            </div>
          </Reveal>
        </motion.div>
      </section>

      {/* SECTION 1 — Understanding [Company] (conditional) */}
      {tuv.showSection1 && (
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 border-t border-black/10">
        <div className="max-w-[1280px] mx-auto">
          <SectionEyebrow num="01">Understanding {tuv.company}</SectionEyebrow>
          <Reveal><h2 className="editorial-h2 text-4xl md:text-6xl mb-14 max-w-4xl">Enabling organisations to adopt technology, manage risk and innovate with confidence.</h2></Reveal>

          {/* Who is TÜV SÜD */}
          <div className="grid grid-cols-12 gap-6 mb-16">
            <Reveal delay={0.05} className="col-span-12 lg:col-span-7">
              <div className="bg-white/50 border border-black/10 p-6 md:p-8 h-full">
                <div className="overline mb-3">— Who they are</div>
                <p className="text-lg md:text-xl leading-relaxed">
                  One of the world's leading testing, inspection and certification (TIC) organisations —
                  helping businesses demonstrate that products, systems and infrastructure are
                  <strong> safe, compliant, high quality</strong> and <strong>sustainable</strong>.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: 30000, s: "+", label: "Employees" },
                  { v: 100, s: "+", label: "Countries" },
                ].map((s, i) => (
                  <div key={i} className="bg-[var(--ink)] text-[var(--bg)] p-6">
                    <div className="text-4xl md:text-5xl font-display leading-none"><Counter to={s.v} suffix={s.s} /></div>
                    <div className="mt-3 text-xs uppercase tracking-[0.18em] opacity-80">{s.label}</div>
                  </div>
                ))}
                <div className="col-span-2 bg-[#C2410C] text-white p-6 relative overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none"
                  />
                  <div className="relative text-5xl md:text-6xl font-display leading-none tracking-tight">€3.4bn</div>
                  <div className="relative mt-3 text-xs uppercase tracking-[0.22em] text-white font-semibold">Revenue · 2024</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Core themes */}
          <Reveal><div className="overline mb-4">— Core themes</div></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-16">
            {THEMES.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.label} delay={i * 0.05}>
                  <div className="group border border-black/15 bg-white/50 p-5 h-full transition-all hover:border-black hover:-translate-y-1">
                    <Icon size={26} className="text-[var(--accent)] transition-transform group-hover:scale-110" />
                    <div className="mt-4 font-display text-lg">{t.label}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Industries */}
          <Reveal><div className="overline mb-4">— Industry &amp; Infrastructure</div></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.label} delay={i * 0.04}>
                  <div className="group flex items-center gap-4 border border-black/15 bg-white/40 p-4 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors">
                    <Icon size={22} className="text-[var(--accent)] group-hover:text-[var(--accent-soft)] shrink-0" />
                    <span className="text-sm font-semibold">{ind.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Insight + Observation */}
          <div className="grid grid-cols-12 gap-6">
            <Reveal delay={0.05} className="col-span-12 lg:col-span-6">
              <div className="relative border-l-4 border-[var(--accent)] pl-6 md:pl-8 py-4">
                <div className="overline mb-3">— What stood out to me</div>
                <p className="text-xl md:text-2xl font-medium leading-snug">
                  TÜV SÜD provides <span className="text-[var(--accent)]">independent expertise</span> that
                  builds confidence and trust between organisations, regulators and customers.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 lg:col-span-6">
              <div className="bg-[var(--ink)] text-[var(--bg)] p-6 md:p-8 h-full">
                <div className="overline text-[var(--bg)]/70 mb-3">— Strategic observation</div>
                <p className="text-base md:text-lg leading-relaxed">
                  The recent global integration into a single Industry &amp; Infrastructure division presents
                  an opportunity to <strong>strengthen collaboration</strong>, align marketing across regions,
                  and communicate a <strong>more consistent customer proposition</strong>.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Strategy 2030 */}
          <div className="mt-16 md:mt-24">
            <SectionEyebrow>Strategy 2030</SectionEyebrow>
            <Reveal>
              <p className="text-lg md:text-2xl leading-snug max-w-4xl mb-10">
                TÜV SÜD's long-term strategy to strengthen its core business while accelerating growth in
                future-focused markets such as <span className="text-[var(--accent)]">digital services, AI,
                cybersecurity, sustainability and energy transition</span>.
              </p>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 mb-14">
              <Reveal className="col-span-12 lg:col-span-7">
                <div className="bg-white/60 border border-black/10 p-6 md:p-8 h-full">
                  <div className="overline mb-4">— The five strategic priorities</div>
                  <ol className="space-y-3">
                    {strategyPriorities.map((p, i) => (
                      <li key={i} className="flex gap-4 border-b border-black/10 pb-3 last:border-0 last:pb-0">
                        <span className="font-mono text-xs text-[var(--accent)] w-6 shrink-0 pt-1">0{i + 1}</span>
                        <span className="text-base md:text-lg font-medium">{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
              <Reveal delay={0.1} className="col-span-12 lg:col-span-5">
                <div className="bg-[var(--ink)] text-[var(--bg)] p-6 md:p-8 h-full">
                  <div className="overline text-[var(--bg)]/70 mb-4">— What it means for TÜV SÜD</div>
                  <ul className="space-y-3">
                    {STRATEGY_MEANS.map((m, i) => (
                      <li key={i} className="flex gap-3 text-sm md:text-base leading-snug">
                        <span className="text-[var(--accent-soft)] shrink-0">↳</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* Competitors */}
            <SectionEyebrow>Key competitors</SectionEyebrow>
            <Reveal>
              <div className="overflow-x-auto border border-black/15">
                <table className="w-full text-left text-sm md:text-base">
                  <thead className="bg-[var(--ink)] text-[var(--bg)]">
                    <tr>
                      <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">Organisation</th>
                      <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">HQ</th>
                      <th className="p-4 font-semibold text-xs uppercase tracking-[0.18em]">Main Differentiator</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPETITORS.map((c, i) => (
                      <tr key={c.name} className={`border-t border-black/10 ${i % 2 === 0 ? "bg-white/60" : "bg-white/30"}`}>
                        <td className="p-4 font-display text-lg">{c.name}</td>
                        <td className="p-4 text-black/70 whitespace-nowrap">{c.hq}</td>
                        <td className="p-4 leading-snug">{c.diff}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      )}

      {/* SECTION 2 — Understanding of role */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[var(--surface)] border-t border-black/10">
        <div className="max-w-[1280px] mx-auto">
          <SectionEyebrow num="02">My understanding of the role</SectionEyebrow>
          <Reveal><h2 className="editorial-h2 text-4xl md:text-6xl mb-14 max-w-4xl">The questions marketing needs to answer.</h2></Reveal>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-16">
            {questions.map((q, i) => (
              <Reveal key={i} delay={(i % 5) * 0.05}>
                <div className="group h-full bg-white border border-black/10 p-4 hover:border-[var(--accent)] hover:shadow-lg transition-all">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] font-mono mb-2">Q{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-sm font-semibold leading-snug group-hover:text-[var(--accent)] transition-colors">{q}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Flow */}
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className="h-px w-8 bg-black/30" />
              <div className="overline">— Flowing into</div>
              <span className="h-px w-8 bg-black/30" />
            </div>
          </Reveal>
          <FlowDiagram />
        </div>
      </section>

      {/* SECTION 3 — How I work */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 border-t border-black/10">
        <div className="max-w-[1280px] mx-auto">
          <SectionEyebrow num="03">How I work</SectionEyebrow>
          <Reveal>
            <h2 className="editorial-h2 text-4xl md:text-6xl mb-6 max-w-4xl">
              Strategic marketing &amp; commercial planning.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg md:text-xl text-black/70 max-w-3xl mb-14 leading-relaxed">
              I work with organisations to turn business objectives into commercially focused marketing strategies —
              partnering with senior stakeholders to plan, deliver and measure.
            </p>
          </Reveal>

          {/* Experience cards */}
          <Reveal><div className="overline mb-4">— Experience</div></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-16">
            {EXPERIENCE.map((x, i) => (
              <Reveal key={x.label} delay={i * 0.05}>
                <ExperienceVignette item={x} index={i} />
              </Reveal>
            ))}
          </div>

          {/* Strengths */}
          <Reveal><div className="overline mb-4">— Core strengths</div></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {strengthsList.map((s, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="group flex items-center gap-3 bg-white border border-black/15 p-4 hover:border-[var(--accent)] hover:-translate-y-1 transition-all">
                  <span className="w-8 h-8 rounded-full bg-[var(--accent)]/10 group-hover:bg-[var(--accent)] group-hover:text-white transition-colors flex items-center justify-center text-[var(--accent)]">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span className="text-sm font-semibold">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Marketing scenario planning — 6-step tabbed framework */}
          <MarketingScenarioPlanning />
        </div>
      </section>

      {/* SECTION 4 — A little more about me */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[var(--surface)] border-t border-black/10 overflow-hidden">
        <NetworkBg />
        <div className="relative max-w-[1280px] mx-auto">
          <SectionEyebrow num="04">A little more about me</SectionEyebrow>
          <Reveal><h2 className="editorial-h2 text-4xl md:text-6xl mb-14 max-w-4xl">Curiosity &amp; practical AI.</h2></Reveal>

          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <Reveal delay={0.05} className="col-span-12 lg:col-span-7">
              <p className="text-lg md:text-xl leading-relaxed">
                I'm naturally curious and enjoy learning, particularly when it comes to emerging technology.
                Over the past few years I've spent a lot of time exploring how AI can solve practical business
                challenges, teaching myself through hands-on experimentation and applying it to real marketing
                and commercial work.
              </p>
              <p className="text-lg md:text-xl leading-relaxed mt-5">
                More recently, I've been developing an <span className="text-[var(--accent)] font-semibold">AI
                Transformation Hub</span> designed for SMEs and third sector organisations. The aim is simple:
                help organisations understand where AI can genuinely add value without requiring specialist
                knowledge or large budgets.
              </p>
              <p className="text-lg md:text-xl leading-relaxed mt-5">
                The approach takes organisations from a basic introduction to AI through assessing their current
                use, identifying opportunities, creating practical playbooks and building tailored workflows for
                areas such as <strong>marketing, operations, recruitment and HR</strong>. The focus is always on
                making everyday work more efficient while ensuring AI is used thoughtfully and where it genuinely
                improves outcomes.
              </p>
            </Reveal>

            {/* AI Transformation Hub — real screenshots */}
            <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
              <AiHubShowcase />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Experience in practice */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 border-t border-black/10">
        <div className="max-w-[1280px] mx-auto">
          <SectionEyebrow num="05">Experience in practice</SectionEyebrow>
          <Reveal>
            <h2 className="editorial-h2 text-4xl md:text-6xl mb-14 max-w-4xl">Three engagements worth walking through.</h2>
          </Reveal>

          <div className="space-y-3">
            {cases.map((c, i) => {
              const isOpen = openCase === c.id;
              return (
                <Reveal key={c.id} delay={i * 0.05}>
                  <div className={`border ${isOpen ? "border-[var(--accent)]" : "border-black/15"} bg-white transition-colors`}>
                    <button
                      className="w-full text-left px-5 md:px-8 py-6 grid grid-cols-12 gap-4 items-center hover:bg-black/[0.02] transition-colors"
                      onClick={() => setOpenCase(isOpen ? null : c.id)}
                      aria-expanded={isOpen}
                      data-testid={`case-toggle-${c.id}`}
                    >
                      <div className="col-span-2 md:col-span-1 font-mono text-xs text-[var(--accent)]">0{i + 1}</div>
                      <div className="col-span-10 md:col-span-5">
                        <div className="font-display text-2xl md:text-3xl">{c.title}</div>
                        <div className="text-xs uppercase tracking-[0.18em] text-black/55 mt-1">{c.tag}</div>
                      </div>
                      <div className="hidden md:block col-span-4 text-sm text-black/60">{c.year}</div>
                      <div className="col-span-12 md:col-span-2 flex justify-end">
                        <ChevronDown
                          size={22}
                          className={`transition-transform ${isOpen ? "rotate-180 text-[var(--accent)]" : "text-black/40"}`}
                        />
                      </div>
                    </button>

                    <motion.div
                      initial={false}
                      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 md:px-8 pb-8 border-t border-black/10 pt-6">
                        {c.id === "rsg" ? (
                          <div className="space-y-8">
                            <div className="grid grid-cols-12 gap-6">
                              <div className="col-span-12 md:col-span-7">
                                <div className="overline mb-3">— Overview</div>
                                <p className="text-base md:text-lg leading-relaxed">{c.overview}</p>
                              </div>
                              <div className="col-span-12 md:col-span-5">
                                <div className="overline mb-3">— Outcome</div>
                                <p className="text-sm md:text-base leading-relaxed text-black/80">{c.outcome}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-12 gap-6">
                              <div className="col-span-12 md:col-span-6">
                                <div className="overline mb-3">— Challenges</div>
                                <ul className="space-y-2">
                                  {c.challenges.map((h, idx) => (
                                    <li key={idx} className="flex gap-3 text-sm border-b border-black/10 pb-2">
                                      <span className="text-[var(--accent)]">↳</span>
                                      <span>{h}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="col-span-12 md:col-span-6">
                                <div className="overline mb-3">— Learnings</div>
                                <ul className="space-y-2">
                                  {c.learnings.map((h, idx) => (
                                    <li key={idx} className="flex gap-3 text-sm border-b border-black/10 pb-2">
                                      <span className="text-[var(--accent)]">↳</span>
                                      <span>{h}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            {c.images?.length > 0 && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {c.images.map((src, idx) => (
                                  <img key={idx} src={src} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover bg-black/5" />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-12 gap-6">
                            <div className="col-span-12 md:col-span-7">
                              <p className="text-base md:text-lg leading-relaxed mb-6">{c.summary}</p>
                              {c.images?.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                  {c.images.map((src, idx) => (
                                    <img key={idx} src={src} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover bg-black/5" />
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="col-span-12 md:col-span-5">
                              <div className="overline mb-3">— Highlights</div>
                              <ul className="space-y-2">
                                {c.highlights.map((h, idx) => (
                                  <li key={idx} className="flex gap-3 text-sm border-b border-black/10 pb-2">
                                    <span className="text-[var(--accent)]">↳</span>
                                    <span>{h}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative py-32 md:py-40 px-6 md:px-10 lg:px-14 bg-[var(--ink)] text-[var(--bg)] overflow-hidden border-t border-black/40">
        <div className="relative max-w-[1280px] mx-auto">
          <Reveal>
            <span className="overline text-[var(--accent-soft)]">— Closing</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="editorial-h1 text-6xl md:text-8xl lg:text-9xl mt-4 leading-[0.95]">Thank you.</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-[var(--bg)]/80">
              Thank you for taking the time to explore this page. I look forward to discussing how my experience,
              strategic approach and curiosity for innovation could contribute to the future success of {tuv.company}.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap items-center gap-6 border-t border-white/15 pt-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--bg)]/60">Mia O'Neill</div>
                <div className="font-display text-2xl">mia.lm.oneill@hotmail.co.uk</div>
              </div>
              <div className="hidden md:block h-8 w-px bg-white/20" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--bg)]/60">LinkedIn</div>
                <a href="https://www.linkedin.com/in/miaoneill/" target="_blank" rel="noopener noreferrer" className="font-display text-2xl link-underline">
                  linkedin.com/in/miaoneill
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-14 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-3 border border-[var(--bg)]/40 hover:border-[var(--accent-soft)] hover:text-[var(--accent-soft)] px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                data-testid="tuv-back-to-top"
              >
                <span className="rotate-180"><ArrowDown size={14} /></span>
                Back to top
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default TuvSud;
